import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact image upload field for the admin panel.
 * - Uploads via authenticated POST /api/admin/upload
 * - Shows a preview if `value` is a URL (from the backend, e.g. /api/files/...)
 * - Emits the returned public URL through `onChange`
 */
const FileUploadField = ({
  label = "Image",
  value,
  onChange,
  authFetch,
  testIdPrefix = "upload",
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewSrc = value ? (value.startsWith("/api/") ? `${BACKEND}${value}` : value) : "";

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image (JPG, PNG, WEBP, GIF)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch("/api/admin/upload", { method: "POST", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Upload failed");
        return;
      }
      const data = await res.json();
      onChange && onChange(data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Network error");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-field`}>
      <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400">
        {label}
      </label>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 shrink-0 rounded border border-neutral-800 bg-black grid place-items-center overflow-hidden">
          {previewSrc ? (
            <img src={previewSrc} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <span className="font-condensed uppercase text-[10px] text-neutral-500">No image</span>
          )}
        </div>
        <div className="flex-1 flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            data-testid={`${testIdPrefix}-input`}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid={`${testIdPrefix}-btn`}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-[10px] px-3 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5" />
            )}
            {uploading ? "Uploading…" : previewSrc ? "Replace" : "Upload"}
          </button>
          {previewSrc && (
            <button
              type="button"
              data-testid={`${testIdPrefix}-clear`}
              onClick={() => onChange && onChange("")}
              className="inline-flex items-center gap-1 text-neutral-500 hover:text-crimson-live font-condensed uppercase tracking-widest text-[10px]"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="text-[10px] text-neutral-500 font-body">
        JPG, PNG, WEBP, or GIF · max 6 MB
      </div>
    </div>
  );
};

export default FileUploadField;
